import {
  DashboardState,
  ECardinalityConversion,
  EDatasourceType,
  ESdChangeReason,
  ESymbolCategory,
  EUserAccessPart,
  IAction,
  IMethodInput,
  IScreenInput,
  ISymbol
} from "@kuika/kuika-cl-model";
import { EApiInputStrategy } from "@kuika/kuika-cl-model/src/enums/api-input-strategy";
import { Guid } from "guid-typescript";
import { ReactNode } from "react";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { getSdInstance } from "../../../../shared/utility/designer-context";
import {
  createDesignerScreenInput,
  deleteDesignerScreenInput,
  updateDesignerScreenInput
} from "../../../../shared/utility/designer-screen-input-actions";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../shared/utility/save-status";
import { ScreenDesignHelper } from "../../../../shared/utility/screen-design-helper";
import KuikaButton, { type KuikaButtonStatus } from "../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaTabContent from "../../../ui-kit/kuika-tab-content/kuika-tab-content";
import KuikaTabs from "../../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaSmallMediumText from "../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import {
  createListWrapper,
  effectiveMethodInputs,
  IElementFieldPath,
  listWrapperOf,
  methodInputCount,
  parseElementFieldPath
} from "../../api-management/api-builder-model";
import ActionSectionEmptyState from "./action-section-empty-state";
import { ActionSectionAddButton, ActionSectionTitle } from "./action-section-header";
import "./action-section.scss";
import { EScreenInputPanelStatus, ScreenInputPanel } from "./screen-input-panel";

export interface IScreenInputListState {
  tmpInput?: IScreenInput;
  dynamicJsonValue?: string;
  dynamicJsonError?: string;
  isDynamicJsonEditing?: boolean;
  /** Where the JSON object's SAVE stands. Owned by `SaveStatus`, never set directly. */
  dynamicJsonSaveStatus: KuikaButtonStatus;
}

export interface IScreenInputListProps {}

/** The Input Type tabs' values, as `KuikaTabs` holds them, in their on-screen order. */
const MANUAL_INPUT_TYPE: string = "false";
const DYNAMIC_INPUT_TYPE: string = "true";
const INPUT_TYPE_ORDER: string[] = [MANUAL_INPUT_TYPE, DYNAMIC_INPUT_TYPE];

interface ISectionCopy {
  title: string;
  addAriaLabel: string;
  /** The empty state's line and its link, for the sections that list inputs themselves. */
  emptyMessage: string;
  emptyActionLabel: string;
}

/** This one component renders three different sections depending on host context (API Builder's
 *  Method Inputs, the Report Designer's Report Inputs, or a screen's own Screen Inputs) — the
 *  header's title and its "+" button's accessible name follow whichever is active. */
const SCREEN_INPUT_SECTION_COPY: Record<"method" | "report" | "screen", ISectionCopy> = {
  method: {
    title: "Method Inputs",
    addAriaLabel: "Add method input",
    emptyMessage: "No method inputs yet.",
    emptyActionLabel: "Add method input"
  },
  report: {
    title: "Report Inputs",
    addAriaLabel: "Add report input",
    emptyMessage: "No report inputs yet.",
    emptyActionLabel: "Add report input"
  },
  screen: {
    title: "Screen Inputs",
    addAriaLabel: "Add screen input",
    emptyMessage: "No screen inputs yet.",
    emptyActionLabel: "Add screen input"
  }
};

/** How many bindings name one of `inputNames` as a top-level method input. */
export const countMethodInputBindings = (actions: IAction[] | undefined, inputNames: string[]): number => {
  let count: number = 0;
  ScreenDesignHelper.forEachActionSymbol(actions, (symbol: ISymbol): void => {
    if (
      symbol.category === ESymbolCategory.inputMethod &&
      symbol.inputName !== undefined &&
      inputNames.includes(symbol.inputName)
    ) {
      count++;
    }
  });
  return count;
};

export interface IListElementBindingMigration {
  migrated: number;
  /** Bindings to the whole list or to a whole element — no single-object counterpart exists. */
  unresolved: number;
}

/**
 * Removing the List of Objects wrapper puts its fields back at the top level, so a binding that
 * read one element's field ("Items → First → Kod") becomes a binding of that field ("Kod") — what
 * it means once the payload is a single object. A binding to the whole list or to a whole element
 * has no such counterpart; it is left as it is and counted for the caller to report.
 */
export const unwrapListElementBindings = (
  actions: IAction[] | undefined,
  wrapperName: string,
  fieldNames: string[]
): IListElementBindingMigration => {
  const result: IListElementBindingMigration = { migrated: 0, unresolved: 0 };
  ScreenDesignHelper.forEachActionSymbol(actions, (symbol: ISymbol): void => {
    if (symbol.category !== ESymbolCategory.inputMethod || symbol.inputName !== wrapperName) return;
    const readsOneElement: boolean =
      symbol.converterOfCardinality === ECardinalityConversion.first ||
      symbol.converterOfCardinality === ECardinalityConversion.last;
    const path: IElementFieldPath | undefined = readsOneElement ? parseElementFieldPath(symbol.fieldName) : undefined;
    if (!path || !fieldNames.includes(path.field)) {
      result.unresolved++;
      return;
    }
    // The picker's "<wrapper>.<first|last>.<field>…" description, minus the element step.
    const step: string = path.converter !== undefined ? `.${ECardinalityConversion[path.converter]}` : "";
    const rest: string = path.rest ? `.${path.rest.replace(/\[(first|last)\]/g, ".$1")}` : "";
    symbol.description = `${path.field}${step}${rest}`;
    symbol.inputName = path.field;
    symbol.converterOfCardinality = path.converter;
    symbol.fieldName = path.rest;
    result.migrated++;
  });
  return result;
};

class ScreenInputList extends PureSdComponent<IScreenInputListProps & IWithSDProps, IScreenInputListState> {
  private readonly dynamicJsonSaveStatus: SaveStatus = new SaveStatus(
    (dynamicJsonSaveStatus: KuikaButtonStatus): void => {
      this.setState({ dynamicJsonSaveStatus });
    }
  );

  constructor(props: IScreenInputListProps & IWithSDProps) {
    super(props);
    this.state = {
      tmpInput: undefined,
      dynamicJsonValue: undefined,
      dynamicJsonError: undefined,
      isDynamicJsonEditing: undefined,
      dynamicJsonSaveStatus: "idle"
    };
  }

  componentWillUnmount(): void {
    this.dynamicJsonSaveStatus.dispose();
  }

  getScreenInputs = (): IScreenInput[] => {
    const screenInputs = this.props.sd.getScreenInputs();
    if (screenInputs) {
      return screenInputs;
    }
    return [];
  };

  constructNewScreenInput = () => {
    this.setState({
      tmpInput: {} as IScreenInput
    });
  };

  handleCancelNew = () => {
    this.setState({ tmpInput: undefined });
  };

  createInput = (input: IScreenInput | IMethodInput) => {
    if (!input) return;
    this.setState({ tmpInput: undefined }, async () => {
      if (!input) return;
      input.id = Guid.create().toString();
      if (this.props.sd?.dashboardState === DashboardState.apiManagement) {
        this.props.sd.createMethodInput(input);
      } else if (
        this.props.sd.dashboardState === DashboardState.dataDiagramScheme &&
        this.props.sd.activeEditingDatasource?.datasourceType === EDatasourceType.BleService &&
        this.props.sd.getSelectedBluetoothMethod()
      ) {
        this.props.sd.createBluetoothMethodInput(input);
      } else {
        // AWAITED: the action resolves on the PERSIST now. `"error" in result` on an
        // unawaited promise is always false, which would silently drop this warning.
        const result = await createDesignerScreenInput(getSdInstance(), input);
        if ("error" in result) {
          KMainFunctions.displayWarningNotification(result.error);
        } else if (!result.persisted) {
          KMainFunctions.displayWarningNotification(result.message);
        }
      }
    });
  };

  updateInput = (input: IScreenInput | IMethodInput, _changeReason: ESdChangeReason) => {
    this.setState({ tmpInput: undefined }, async () => {
      if (!input) return;
      if (this.props.sd?.dashboardState === DashboardState.apiManagement) {
        this.props.sd.updateMethodInput(input);
      } else if (
        this.props.sd.dashboardState === DashboardState.dataDiagramScheme &&
        this.props.sd.activeEditingDatasource?.datasourceType === EDatasourceType.BleService &&
        this.props.sd.getSelectedBluetoothMethod()
      ) {
        this.props.sd.updateBluetoothMethodInput(input);
      } else {
        const result = await updateDesignerScreenInput(getSdInstance(), input);
        if ("error" in result) {
          KMainFunctions.displayWarningNotification(result.error);
        } else if (!result.persisted) {
          KMainFunctions.displayWarningNotification(result.message);
        }
      }
    });
  };

  deleteInput = async (input: IScreenInput) => {
    if (!input) return;
    if (this.props.sd?.dashboardState === DashboardState.apiManagement) {
      this.props.sd.deleteMethodInput(input);
    } else if (
      this.props.sd.dashboardState === DashboardState.dataDiagramScheme &&
      this.props.sd.activeEditingDatasource?.datasourceType === EDatasourceType.BleService &&
      this.props.sd.getSelectedBluetoothMethod()
    ) {
      this.props.sd.deleteBluetoothMethodInput(input);
    } else {
      const result = await deleteDesignerScreenInput(getSdInstance(), input.id);
      if ("error" in result) {
        KMainFunctions.displayWarningNotification(result.error);
      } else if (!result.persisted) {
        // A delete that did not persist comes BACK on the next load; saying nothing
        // here would show the row disappearing and then reappearing with no reason.
        KMainFunctions.displayWarningNotification(result.message);
      }
    }
  };

  getSelectedDisplayItem = () => {
    const sd = this.props.sd.getScreenDesign();
    const selectedDisplayItem = this.props.sd.getSelectedDisplayItem(sd);
    return selectedDisplayItem;
  };

  getIsDynamicMode = (): boolean => {
    const apiMethod = this.props.sd.getSelectedApiMethod();
    if (!apiMethod) return false;
    const entry = this.props.sd.getDynamicInputForMethod(apiMethod.id);
    return entry?.isDynamic ?? false;
  };

  handleDynamicSwitchChange = (checked: boolean) => {
    const apiMethod = this.props.sd.getSelectedApiMethod();
    if (!apiMethod) return;
    const entry = this.props.sd.getDynamicInputForMethod(apiMethod.id);
    this.props.sd.saveDynamicInputForMethod(apiMethod.id, {
      isDynamic: checked,
      jsonValue: entry?.jsonValue ?? ""
    });
    this.setState({ isDynamicJsonEditing: undefined, dynamicJsonValue: undefined, dynamicJsonError: undefined });
  };

  getDynamicJsonValue = (): string => {
    const apiMethod = this.props.sd.getSelectedApiMethod();
    if (!apiMethod) return "";
    const entry = this.props.sd.getDynamicInputForMethod(apiMethod.id);
    return this.state.dynamicJsonValue !== undefined ? this.state.dynamicJsonValue : (entry?.jsonValue ?? "");
  };

  handleDynamicJsonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    this.setState({ dynamicJsonValue: value, dynamicJsonError: undefined });
  };

  /** Returns true when the JSON textarea should be editable (not yet saved or user clicked EDIT) */
  getIsDynamicJsonEditing = (): boolean => {
    // If explicitly set via state, use that
    if (this.state.isDynamicJsonEditing !== undefined) {
      return this.state.isDynamicJsonEditing;
    }
    // Otherwise, check if there's already a saved JSON value with generated inputs → read-only
    const apiMethod = this.props.sd.getSelectedApiMethod();
    if (!apiMethod) return true;
    const entry = this.props.sd.getDynamicInputForMethod(apiMethod.id);
    const hasSavedJson = entry?.jsonValue?.trim();
    const hasGeneratedInputs = apiMethod.inputMethods && apiMethod.inputMethods.length > 0;
    // If we have saved JSON AND generated inputs → start in read-only mode
    if (hasSavedJson && hasGeneratedInputs) return false;
    // Otherwise → start in editing mode
    return true;
  };

  handleDynamicJsonEdit = () => {
    this.setState({ isDynamicJsonEditing: true });
  };

  sanitizeJsonString = (raw: string): string => {
    return (
      raw
        // Remove BOM (Byte Order Mark)
        .replace(/^\uFEFF/, "")
        // Remove zero-width characters
        .replace(/[\u200B\u200C\u200D\uFEFF]/g, "")
        // Replace smart/curly double quotes with standard double quotes
        .replace(/[\u201C\u201D\u201E\u201F\u2033\u2036]/g, '"')
        // Replace smart/curly single quotes with standard single quotes
        .replace(/[\u2018\u2019\u201A\u201B\u2032\u2035]/g, "'")
        // Replace non-breaking space with regular space
        .replace(/\u00A0/g, " ")
    );
  };

  // SAVE holds its ✓ over the object it saved; only then does the editor fall back to read-only
  // with EDIT in SAVE's place.
  handleDynamicJsonSave = () => {
    const apiMethod = this.props.sd.getSelectedApiMethod();
    if (!apiMethod) return;
    // Live again once the ✓ replaces the spinner, so a second press would save the object twice.
    if (!this.dynamicJsonSaveStatus.canStart) return;

    const rawJsonValue = this.getDynamicJsonValue();
    const jsonValue = this.sanitizeJsonString(rawJsonValue);

    if (jsonValue.trim()) {
      try {
        JSON.parse(jsonValue);
      } catch {
        this.setState({ dynamicJsonError: "Invalid JSON format. Please enter a valid JSON object." });
        return;
      }
    }

    this.dynamicJsonSaveStatus.start();
    this.props.sd.saveDynamicInputForMethod(apiMethod.id, {
      isDynamic: true,
      jsonValue
    });
    // SAVE's ✓ over the object, then the read-only object and its generated inputs, say it; a toast
    // only repeated them.
    this.dynamicJsonSaveStatus.succeed((): void =>
      this.setState({ dynamicJsonValue: undefined, dynamicJsonError: undefined, isDynamicJsonEditing: false })
    );
  };

  handleToggleInputStrategy = (strategy: EApiInputStrategy) => {
    const apiMethod = this.props.sd.getSelectedApiMethod();
    if (!apiMethod) return;

    // The wrapper stays invisible: the panel lists its fields (`effectiveMethodInputs`) and the "+"
    // adds into it (`addMethodInput`); only the picker and the generated code see `Items` itself.
    const existingWrapper: IMethodInput | undefined = listWrapperOf(apiMethod);
    if (
      strategy === EApiInputStrategy.ListOfObjects &&
      !existingWrapper &&
      apiMethod.inputMethods &&
      apiMethod.inputMethods.length > 0
    ) {
      const movedInputNames: string[] = apiMethod.inputMethods.map((input: IMethodInput): string => input.name);
      const wrapperParameter: IMethodInput = createListWrapper(apiMethod, [...apiMethod.inputMethods]);
      apiMethod.inputMethods = [wrapperParameter];
      // Which element a binding should now read (First or Last) is the user's call, so the
      // bindings are reported, not rewritten; validation (KV1134) flags them until rebound.
      const staleCount: number = countMethodInputBindings(apiMethod.actions, movedInputNames);
      if (staleCount > 0) {
        const one: boolean = staleCount === 1;
        KMainFunctions.displayWarningNotification(
          `${staleCount} ${one ? "binding points" : "bindings point"} at inputs that are now inside the list. ` +
            `Rebind ${one ? "it" : "them"} through ${wrapperParameter.name} → First or Last.`
        );
      }
    } else if (strategy === EApiInputStrategy.Object && existingWrapper) {
      const wrapper: IMethodInput = existingWrapper;
      const fields: IMethodInput[] = wrapper.parameters ?? [];
      apiMethod.inputMethods = fields;
      // "Items → First → Kod" reads "Kod" once the payload is a single object, so those bindings
      // follow the fields out of the wrapper; only whole-list/whole-element ones cannot.
      const migration: IListElementBindingMigration = unwrapListElementBindings(
        apiMethod.actions,
        wrapper.name,
        fields.map((field: IMethodInput): string => field.name)
      );
      if (migration.unresolved > 0) {
        const one: boolean = migration.unresolved === 1;
        KMainFunctions.displayWarningNotification(
          `${migration.unresolved} ${one ? "binding reads" : "bindings read"} the whole list and ` +
            `${one ? "has" : "have"} no single-object counterpart. Rebind ${one ? "it" : "them"} to a method input.`
        );
      }
    }
    apiMethod.inputStrategy = strategy;
    this.props.sd.saveApiMethod(apiMethod);
  };

  renderApiMethodInputs = () => {
    const apiMethod = this.props.sd.getSelectedApiMethod();
    if (!apiMethod) return null;
    const renderInputs: IMethodInput[] = effectiveMethodInputs(apiMethod);

    return renderInputs.map((input) => {
      return (
        <div key={input.id}>
          <ScreenInputPanel
            screenInput={input}
            screenInputPanelStatus={EScreenInputPanelStatus.edit}
            onCancel={() => this.deleteInput(input)}
            onScreenInputSaved={this.updateInput}
            descriptionEnabled
          />
        </div>
      );
    });
  };

  /** The rows under the header: an API method's inputs, a BLE method's, or the screen/report's own. */
  renderInputRows = (
    screenInputs: IScreenInput[],
    sectionCopy: ISectionCopy,
    isApiMethodContext: boolean,
    isBleInputContext: boolean
  ): React.ReactNode => {
    if (isApiMethodContext) {
      return this.renderApiMethodInputs();
    }
    if (isBleInputContext) {
      const bleMethod = this.props.sd.getSelectedBluetoothMethod();
      return bleMethod?.bluetoothInfo?.inputs.map((input) => (
        <div key={input.id}>
          <ScreenInputPanel
            screenInput={input}
            screenInputPanelStatus={EScreenInputPanelStatus.edit}
            onCancel={() => this.deleteInput(input)}
            onScreenInputSaved={this.updateInput}
          />
        </div>
      ));
    }
    if (screenInputs.length === 0 && !this.state.tmpInput) {
      return (
        <ActionSectionEmptyState
          message={sectionCopy.emptyMessage}
          actionLabel={sectionCopy.emptyActionLabel}
          onAdd={this.constructNewScreenInput}
          testId="screenInputsEmptyState"
        />
      );
    }
    return screenInputs.map((input) => (
      <div key={input.id}>
        <ScreenInputPanel
          screenInput={input}
          screenInputPanelStatus={EScreenInputPanelStatus.edit}
          onCancel={() => this.deleteInput(input)}
          onScreenInputSaved={this.updateInput}
        />
      </div>
    ));
  };

  render() {
    if (!this.props || !this.props.sd) {
      return <></>;
    }
    const selectedDisplayItem = this.getSelectedDisplayItem();
    if (!selectedDisplayItem) {
      return <></>;
    }
    const screenInputs = this.getScreenInputs();
    const api = this.props.sd.getSelectedApi();
    const apiMethod = this.props.sd.getSelectedApiMethod();
    const ds = this.props.sd.activeEditingDatasource;
    const bleMethod = this.props.sd.getSelectedBluetoothMethod();
    const hasAccessDataBinding = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.BindDataToComponent,
      this.props.sd.project?.projectAccess
    );
    if (!hasAccessDataBinding) {
      return <></>;
    }

    const isApiMethodContext = api && apiMethod && this.props.sd.dashboardState === DashboardState.apiManagement;
    const isDynamicMode = isApiMethodContext ? this.getIsDynamicMode() : false;
    const isBleInputContext: boolean = !!(bleMethod && ds?.datasourceType === EDatasourceType.BleService);
    const isMethodInputs: boolean = !!isApiMethodContext || isBleInputContext;
    const isReportInputs: boolean = !isMethodInputs && this.props.sd.dashboardState === DashboardState.reportDesigner;
    let sectionCopy: ISectionCopy = SCREEN_INPUT_SECTION_COPY.screen;
    if (isMethodInputs) {
      sectionCopy = SCREEN_INPUT_SECTION_COPY.method;
    } else if (isReportInputs) {
      sectionCopy = SCREEN_INPUT_SECTION_COPY.report;
    }

    // Mirrors the same three-way branch the list below renders from (isApiMethodContext / BLE /
    // plain screenInputs), so the header's count never drifts from what is actually listed.
    let itemCount: number = screenInputs.length;
    if (isApiMethodContext) {
      itemCount = apiMethod ? methodInputCount(apiMethod) : 0;
    } else if (isBleInputContext) {
      itemCount = bleMethod?.bluetoothInfo?.inputs.length ?? 0;
    }

    return (
      <>
        <div className="kuika_Wrapper">
          <div className="kuika_actionPanelWrapperHeader kuika-action-section__header kuika-action-section__header--inputs-top">
            <div className="kuika-action-section__header-row">
              <ActionSectionTitle title={sectionCopy.title} count={itemCount} />
              {!isDynamicMode && (
                <ActionSectionAddButton
                  ariaLabel={sectionCopy.addAriaLabel}
                  testId="addInputButton"
                  onClick={this.constructNewScreenInput}
                />
              )}
            </div>
            <div>
              {isApiMethodContext && (
                <div style={{ display: "flex", marginTop: 8, marginBottom: 8, alignItems: "center" }}>
                  <KuikaSmallText style={{ flex: "1" }}>Input Type</KuikaSmallText>
                  <span style={{ flex: "2" }}>
                    <KuikaTabs
                      options={[
                        {
                          value: "false",
                          icon: "Manual"
                        },
                        {
                          value: "true",
                          icon: "Dynamic"
                        }
                      ]}
                      onChange={(val) => {
                        val === "true" ? this.handleDynamicSwitchChange(true) : this.handleDynamicSwitchChange(false);
                      }}
                      value={isDynamicMode ? "true" : "false"}
                    />
                  </span>
                </div>
              )}
            </div>
            {!isDynamicMode && isApiMethodContext && (
              <div
                className="kuika_actionPanelWrapperHeader"
                style={{ display: "flex", marginTop: 8, marginBottom: 8, alignItems: "center" }}
              >
                <KuikaSmallText style={{ flex: "1" }}>Payload As</KuikaSmallText>
                <span style={{ flex: "2" }}>
                  <KuikaTabs
                    options={[
                      {
                        value: "false",
                        icon: "Object"
                      },
                      {
                        value: "true",
                        icon: "List of Objects"
                      }
                    ]}
                    onChange={(val) => {
                      val === "true"
                        ? this.handleToggleInputStrategy(EApiInputStrategy.ListOfObjects)
                        : this.handleToggleInputStrategy(EApiInputStrategy.Object);
                    }}
                    value={apiMethod?.inputStrategy === EApiInputStrategy.ListOfObjects ? "true" : "false"}
                  />
                </span>
              </div>
            )}
          </div>

          <div>
            <KuikaTabContent
              activeKey={isDynamicMode ? DYNAMIC_INPUT_TYPE : MANUAL_INPUT_TYPE}
              order={INPUT_TYPE_ORDER}
              render={(inputType: string): ReactNode =>
                inputType === DYNAMIC_INPUT_TYPE ? (
                  (() => {
                    const isEditing = this.getIsDynamicJsonEditing();
                    // The object being saved is not edited under its own ✓.
                    const isDynamicJsonSaving: boolean = this.state.dynamicJsonSaveStatus !== "idle";
                    return (
                      <div style={{ padding: "8px var(--gap-m)" }}>
                        <KuikaSmallMediumText style={{ marginBottom: 4, display: "block" }}>
                          JSON Object
                        </KuikaSmallMediumText>
                        <KuikaInput
                          type="textarea"
                          value={this.getDynamicJsonValue()}
                          onChange={this.handleDynamicJsonChange}
                          placeholder={'{\n  "paramName": "String",\n  "paramName2": "Int32"\n}'}
                          autoSize={{ minRows: 6, maxRows: 16 }}
                          disabled={!isEditing || isDynamicJsonSaving}
                          style={{
                            width: "100%",
                            fontFamily: "monospace",
                            fontSize: 12,
                            background: isEditing ? "var(--dark-0)" : "var(--dark-1)",
                            border: this.state.dynamicJsonError ? "1px solid var(--red-5, #ff4d4f)" : undefined,
                            color: !isEditing ? "var(--dark-7)" : undefined
                          }}
                          data-testid="dynamicInputJsonTextarea"
                        />
                        {this.state.dynamicJsonError && (
                          <KuikaSmallMediumText
                            style={{ color: "var(--red-5, #ff4d4f)", marginTop: 4, display: "block" }}
                          >
                            {this.state.dynamicJsonError}
                          </KuikaSmallMediumText>
                        )}
                        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                          {isEditing ? (
                            <KuikaButton
                              type="secondary"
                              size="sm"
                              status={this.state.dynamicJsonSaveStatus}
                              onClick={this.handleDynamicJsonSave}
                            >
                              <KuikaSmallMediumText>SAVE</KuikaSmallMediumText>
                            </KuikaButton>
                          ) : (
                            <KuikaButton type="secondary" size="sm" onClick={this.handleDynamicJsonEdit}>
                              <KuikaSmallMediumText>EDIT</KuikaSmallMediumText>
                            </KuikaButton>
                          )}
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  <>
                    {this.state.tmpInput && (
                      <ScreenInputPanel
                        screenInput={this.state.tmpInput}
                        screenInputPanelStatus={EScreenInputPanelStatus.new}
                        onCancel={this.handleCancelNew}
                        onScreenInputSaved={this.createInput}
                        descriptionEnabled={this.props.sd.dashboardState === DashboardState.apiManagement}
                      />
                    )}
                    {this.renderInputRows(screenInputs, sectionCopy, !!isApiMethodContext, isBleInputContext)}
                  </>
                )
              }
            />
          </div>
        </div>
      </>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      // Adding or deleting an input changes the header's count and whether the empty state shows.
      changeReason === ESdChangeReason.ChangeScreenInput
    ) {
      return true;
    }
    return false;
  };
}

const screenInputList = withSD<IScreenInputListProps>(ScreenInputList);
export { screenInputList as ScreenInputList };
